import type {
  BookUpdate,
  CommandResult,
  Envelope,
  FlowNotice,
  FollowSignalEvent,
  LockSignalEvent,
  OpsAlertEvent,
  Order,
  OrderUpdate,
  ProfileUpdate,
  Trade,
  TradeEvent,
} from "../api/types";
import { parse } from "../lib/money";
import type { FlowVersion } from "../lib/participants";
import { useBooks } from "./useBooks";
import { useConn } from "./useConn";
import { useFeed } from "./useFeed";
import { useFills } from "./useFills";
import { useLockSignals } from "./useLockSignals";
import { useOpsAlerts } from "./useOpsAlerts";
import { useOrders } from "./useOrders";
import { useSignals } from "./useSignals";
import { useTape } from "./useTape";
import { useTiers } from "./useTiers";

/**
 * Единственная точка входа сообщений сокета.
 *
 * Разводка по типу идёт ДО дедупликации по stream_id, и это существенно:
 * сообщения стакана приходят из Pub/Sub, где идентификаторов записей не бывает
 * вовсе, поэтому isDuplicate пропускает их не глядя. Свали их в общий список —
 * и диагностическая панель на 500 строк переписывалась бы десятки раз в секунду.
 * Идемпотентность стакана обеспечивает seq, а не набор stream_id.
 *
 * Всё, что лента и стакан отвергли как неполное, падает в диагностику: ничего
 * не исчезает молча и ничто не роняет рендер.
 */

/**
 * Дедупликация на границе «история ∩ живой поток»: web_api сначала регистрирует
 * клиента, затем читает историю, поэтому событие может прийти дважды — с одним
 * и тем же stream_id. Память ограничена: набор помнит последние SEEN_LIMIT
 * ключей, чего с запасом хватает на окно перехлёста (доли секунды).
 */
const SEEN_LIMIT = 1000;
const seen = new Set<string>();
const seenOrder: string[] = [];

function isDuplicate(env: Envelope): boolean {
  if (!env.stream_id) return false;
  const key = `${env.type}:${env.stream_id}`;
  if (seen.has(key)) return true;
  seen.add(key);
  seenOrder.push(key);
  if (seenOrder.length > SEEN_LIMIT) {
    const oldest = seenOrder.shift();
    if (oldest !== undefined) seen.delete(oldest);
  }
  return false;
}

/** Сделка пригодна к показу, только если есть всё, из чего состоит строка ленты. */
function isRenderableTrade(t: Partial<Trade> | undefined): t is Trade {
  return Boolean(t?.id && t.condition_id && t.side && t.price && t.size);
}

function isRenderableBook(u: Partial<BookUpdate> | null): u is BookUpdate {
  return Boolean(u?.asset_id) && typeof u?.seq === "number";
}

/** Ордер пригоден к показу, только если есть всё, из чего состоит строка панели. */
function isRenderableOrder(o: Partial<Order> | undefined): o is Order {
  return Boolean(o?.client_order_id && o.condition_id && o.status && o.price && o.size);
}

/** Выросло ли исполненное количество ордера: это и есть признак филла. */
function grewMatched(before: string | undefined, after: string | undefined): boolean {
  const a = parse(after);
  if (a === null || a <= 0) return false;
  const b = parse(before) ?? 0;
  return a > b;
}

/**
 * Кому сказать «перечитай позиции».
 *
 * Через колбэк, а не прямым обращением к TanStack Query: dispatch — чистая
 * разводка сообщений без сети и без React, и импорт клиента запросов сюда завёл бы
 * зависимость от порядка инициализации приложения. Ставит колбэк тот, у кого есть
 * клиент (см. hooks/usePositions.ts).
 */
type PositionsRefresher = (conditionID: string) => void;
let refreshPositions: PositionsRefresher | null = null;

/** Подписывает обновление позиций на события исполнения. */
export function setPositionsRefresher(fn: PositionsRefresher | null): void {
  refreshPositions = fn;
}

function requestPositionsRefresh(conditionID: string | undefined): void {
  if (refreshPositions && conditionID) refreshPositions(conditionID);
}

/**
 * Кому сказать «перечитай накопление по рынку». Тот же приём и тот же довод,
 * что у позиций: колбэк ставит хук с клиентом запросов (hooks/useParticipants.ts).
 */
/**
 * Вторым аргументом — состояние окна из стука (epoch/seq), чтобы получатель
 * мог сверить его с последним ответом и не перечитывать то, что уже на
 * экране. Необязательное: трейдер прежней версии стучит без номеров.
 */
type ParticipantsRefresher = (conditionID: string, notice?: Partial<FlowVersion>) => void;
let refreshParticipants: ParticipantsRefresher | null = null;

/** Подписывает перечитывание накопления на стук трейдера «окно изменилось». */
export function setParticipantsRefresher(fn: ParticipantsRefresher | null): void {
  refreshParticipants = fn;
}

/**
 * Кому сказать «перечитай ряд накопления». Отдельный колбэк от панели, а не
 * второй вызов того же: у ряда СВОЯ склейка стуков (секунда против ста
 * миллисекунд), потому что его ответ весит на два порядка больше свода.
 */
type TimelineRefresher = (conditionID: string, notice?: Partial<FlowVersion>) => void;
let refreshTimeline: TimelineRefresher | null = null;

/** Подписывает перечитывание ряда на тот же стук. */
export function setTimelineRefresher(fn: TimelineRefresher | null): void {
  refreshTimeline = fn;
}

export function dispatch(env: Envelope): void {
  if (isDuplicate(env)) return;
  useConn.getState().bumpReceived();

  switch (env.type) {
    case "book_snapshot":
    case "book_delta": {
      const u = env.payload as Partial<BookUpdate> | null;
      if (!isRenderableBook(u)) {
        useFeed.getState().push(env);
        return;
      }
      useBooks.getState().ingest(env.type, u);
      return;
    }

    case "trade": {
      const event = env.payload as Partial<TradeEvent> | null;
      const trade = event?.trade;
      if (!isRenderableTrade(trade)) {
        useFeed.getState().push(env);
        return;
      }
      // Тиры уезжают в отдельный стор, а не в строку ленты: незнакомый кошелёк
      // приходит БЕЗ тира и получает его секундами позже сообщением
      // profile_update, а строка, запомнившая тир навсегда, так и осталась бы
      // без квалификации до перезагрузки вкладки (см. useTiers).
      useTiers.getState().ingest(event?.counterparty_tiers);
      useTape.getState().push(trade);
      // НАШИ сделки — ещё и в стор филлов: из него считается позиция, которую
      // видно сразу, не дожидаясь чужого индексатора. Лента для этого не годится:
      // её буфер общий на вкладку и за активное окно вытесняется, то есть
      // позиция уменьшалась бы сама (см. useFills).
      useFills.getState().push(trade);
      // И просим перечитать снимок индексатора: момент, когда смотреть стоит,
      // известен точно — сразу после исполнения. Прежде инвалидация висела на
      // ПРИЁМЕ команды, то есть на постановке ордера: для мейкера, чей ордер в
      // этот момент только встал в стакан, она не значила ничего, а на сам филл
      // не срабатывало ничего вовсе.
      if (trade.ours) requestPositionsRefresh(trade.condition_id);
      return;
    }

    case "profile_update": {
      const profile = (env.payload as Partial<ProfileUpdate> | null)?.profile;
      if (!profile?.address || !profile.tier) {
        useFeed.getState().push(env);
        return;
      }
      // Это и есть то сообщение, ради которого заведён мост md.profiles: оно
      // дорисовывает «неизвестен» в уже нарисованных строках ленты.
      useTiers.getState().set(profile.address, profile.tier);
      return;
    }

    case "follow_signal": {
      const signal = (env.payload as Partial<FollowSignalEvent> | null)?.signal;
      if (!signal?.id || !signal.outcome) {
        useFeed.getState().push(env);
        return;
      }
      // Замещение по id, а не добавление: одним типом приезжают ВСЕ состояния
      // карточки — появилась, взята, пропущена, сгорела. Добавляй мы каждое
      // сообщение строкой, на экране висели бы четыре копии одного сигнала, из
      // которых первая — с живыми кнопками.
      useSignals.getState().ingest(signal);
      return;
    }

    case "lock_signal": {
      const signal = (env.payload as Partial<LockSignalEvent> | null)?.signal;
      if (!signal?.id || !signal.outcome) {
        useFeed.getState().push(env);
        return;
      }
      // Замещение по id, как у сигнала кита, и по той же причине: одним типом
      // приезжают ВСЕ состояния карточки — появилась, доросла с safe до settled,
      // взята, пропущена, отозвана, сгорела.
      useLockSignals.getState().ingest(signal);
      return;
    }

    case "ops_alert": {
      const alert = (env.payload as Partial<OpsAlertEvent> | null)?.alert;
      if (!alert?.id || !alert.rule_id) {
        useFeed.getState().push(env);
        return;
      }
      // Замещение по id, как у карточки замка: тревога и её СНЯТИЕ приезжают
      // одним типом с одним идентификатором, и склеивать их по порядку
      // доставки, которого у сокета нет, пришлось бы руками.
      useOpsAlerts.getState().ingest(alert);
      return;
    }

    case "order_update": {
      const order = (env.payload as Partial<OrderUpdate> | null)?.order;
      if (!isRenderableOrder(order)) {
        useFeed.getState().push(env);
        return;
      }
      const before = useOrders.getState().orders[order.client_order_id];
      useOrders.getState().ingestOrder(order);
      // Выросшее size_matched — это исполнение, и снимок индексатора надо
      // перечитать. Событие ордера приходит и без сделки (LIVE, CANCELED), поэтому
      // тут именно сравнение, а не «пришло обновление»: круг за каждым переходом
      // статуса стоил бы чужому индексатору лишних запросов ни за чем.
      if (grewMatched(before?.size_matched, order.size_matched)) {
        requestPositionsRefresh(order.condition_id);
      }
      // В диагностику НЕ дублируется, в отличие от command_result: при частичных
      // исполнениях обновления идут пачками и вытеснили бы из панели всё
      // остальное.
      return;
    }

    case "command_result": {
      const res = env.payload as Partial<CommandResult> | null;
      if (!res || typeof res.accepted !== "boolean") {
        useFeed.getState().push(env);
        return;
      }
      useOrders.getState().ingestResult(env.trace_id ?? "", res as CommandResult);
      // И ВСЁ РАВНО в диагностику: результат команды редок и бесценен при
      // разборе полёта, а панель диагностики — единственное место, где видна
      // его сырая форма.
      //
      // Заметим: у command_result нет condition_id, поэтому web_api рассылает
      // его глобально — каждая вкладка видит каждый результат. В однооператорном
      // MVP это намеренно, и это же причина, по которой ключом корреляции обязан
      // быть trace_id, а не «последняя команда, которую отправил я».
      useFeed.getState().push(env);
      return;
    }

    case "market_flow_delta": {
      // Стук, а не данные: панель перечитывает свод сама. В диагностику не
      // падает — десять стуков в секунду на окно вытеснили бы из неё всё.
      const notice = env.payload as Partial<FlowNotice> | null;
      if (notice?.condition_id) {
        // Стук уезжает получателям целиком: его epoch/seq — те же поля, что
        // у FlowVersion, и по ним отсеивается уже известное состояние окна.
        if (refreshParticipants) refreshParticipants(notice.condition_id, notice);
        // Ряд будится ТЕМ ЖЕ стуком, а не своим таймером: два независимых
        // источника «пора перечитать» дали бы график и таблицу, обновлённые в
        // разные моменты, — то есть последнюю точку линии, не совпадающую с
        // числом под ней ровно на такт расхождения.
        if (refreshTimeline) refreshTimeline(notice.condition_id, notice);
      }
      return;
    }

    default:
      // pong и всё неизвестное.
      useFeed.getState().push(env);
  }
}
